import { useState, useRef, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  CheckCheck,
  AlertCircle,
  RotateCcw,
  MessageSquare,
  CheckSquare,
  Smile,
  MoreHorizontal,
  Pin,
  Pencil,
  Trash2,
  Copy,
  Quote,
  Download,
  ImageDown,
  FileText,
  FileArchive,
  Image as ImageIcon,
  Film,
  Code as CodeIcon,
  Sparkles,
  Phone,
  ListChecks,
  Megaphone,
  Info,
  Reply,
  Flag,
  Ban,
} from "lucide-react";
import { cn } from "@/lib/cn";
import type { Message } from "@/lib/types";
import { isIntegrationMessage } from "@/lib/message-sender";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { formatRelative, formatBytes } from "@/lib/format";
import { authenticatedFetch, resolveApiUrl } from "@/lib/api";


/**
 * 12 Chatwork-killer message kinds — see docs/chatwork_killer/05_core_chat_features.md §2.
 */
export type MessageKind =
  | "text"
  | "rich"
  | "code"
  | "image"
  | "video"
  | "file"
  | "system"
  | "task"
  | "call_summary"
  | "ai_summary"
  | "poll"
  | "announce";

/** Optional richer view-payload glued onto a Message for rendering. */
export interface MessageView extends Message {
  kind?: MessageKind;
  /** code-block language (kind=code). */
  language?: string;
  /** kind=image / video / file. */
  attachments?: Array<{
    id?: string;
    url?: string;
    name: string;
    mime?: string;
    size?: number;
    width?: number;
    height?: number;
  }>;
  /** kind=poll. */
  poll?: { question: string; options: { id: string; label: string; votes: number }[]; voted?: string };
  /** kind=task. */
  task?: { id?: string; title: string; status: "todo" | "in_progress" | "review" | "done"; due?: string };
  /** kind=system. */
  systemKind?: "join" | "leave" | "rename" | "pin" | "archive" | "role_change";
  /** kind=call_summary. */
  call?: { durationSec: number; participants: number; minutesUrl?: string };
  /** kind=ai_summary. */
  aiSummary?: { tldr: string; bullets: string[] };
  /** Edit + thread metadata. */
  editedAt?: string;
  replyCount?: number;
  pinned?: boolean;
}

export interface MessageItemProps {
  message: MessageView;
  /** Show the avatar / header (false = grouped continuation row). */
  showHeader?: boolean;
  unread?: boolean;
  onRetry?: (localId: string) => void;
  onTaskify?: (id: string) => void;
  onReply?: (id: string) => void;
  onOpenThread?: (id: string) => void;
  onReact?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onPin?: (id: string) => void;
  onCopy?: (id: string) => void;
  onQuote?: (id: string) => void;
  /** Report this message (hidden for the viewer's own messages). */
  onReport?: (id: string) => void;
  /** Block the author of this message (hidden for the viewer's own messages). */
  onBlock?: (id: string) => void;
  /** True when the signed-in member posted this message. */
  isOwn?: boolean;
}

/* ------------------------------------------------------------------ */
/* Body renderers (one per kind)                                       */
/* ------------------------------------------------------------------ */

/** Cheap, dependency-free Markdown subset — bold/italic/code/links/lists. */
function renderInline(src: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))|(https?:\/\/[^\s<>"'`「」『』【】\[\]]+)|(@[^\s]+)/gi;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(src))) {
    if (m.index > last) out.push(src.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("`")) {
      out.push(
        <code
          key={`c${key++}`}
          className="px-1 py-0.5 rounded bg-surface-raised text-fg-primary text-[0.92em] font-mono"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("**")) {
      out.push(
        <strong key={`b${key++}`} className="font-semibold">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("*")) {
      out.push(
        <em key={`i${key++}`} className="italic">
          {tok.slice(1, -1)}
        </em>,
      );
    } else if (tok.startsWith("[") || /^https?:\/\//i.test(tok)) {
      const m2 = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      let href = m2 ? m2[2] : tok.replace(/[.,!;:。、）]+$/, "");
      // Keep balanced URL parentheses, but leave a surrounding ')' as text.
      while (!m2 && href.endsWith(")") && href.split(")").length > href.split("(").length) {
        href = href.slice(0, -1);
      }
      if (/^(https?:|mailto:)/i.test(href)) {
        out.push(
          <a
            key={`a${key++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-700 underline hover:text-brand-600 break-words"
          >
            {m2 ? m2[1] : href}
          </a>,
        );
        if (!m2 && href.length < tok.length) out.push(tok.slice(href.length));
      } else {
        out.push(tok);
      }
    } else if (tok.startsWith("@")) {
      out.push(
        <span
          key={`m${key++}`}
          className="px-1 py-0.5 rounded bg-brand-50 text-brand-700 font-medium"
        >
          {tok}
        </span>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

type FenceSegment = { code: false; text: string } | { code: true; text: string; language?: string };

/**
 * Split a body on ``` fences. Fences may sit mid-line (Slack / Chatwork style, e.g. "```note text```"),
 * and one message may hold several code blocks. A trailing fence with no partner stays literal text.
 * A short ASCII word right after an opening fence followed by a newline is the language ("```ts\n").
 */
function splitFences(src: string): FenceSegment[] {
  const parts = src.split("```");
  if (parts.length < 3) return [{ code: false, text: src }];
  const out: FenceSegment[] = [];
  const pushText = (text: string) => {
    const prev = out[out.length - 1];
    if (prev && !prev.code) prev.text += text;
    else out.push({ code: false, text });
  };
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i % 2 === 0) {
      pushText(part);
    } else if (i <= parts.length - 2) {
      const lang = /^([A-Za-z0-9_+#.-]{1,20})\r?\n/.exec(part);
      let text = lang ? part.slice(lang[0].length) : part.replace(/^\r?\n/, "");
      text = text.replace(/\r?\n$/, "");
      out.push({ code: true, text, language: lang?.[1] });
    } else {
      pushText("```" + part);
    }
  }
  // Drop the line break that only separates text from an adjacent fence.
  return out
    .map((seg, i) => {
      if (seg.code) return seg;
      let text = seg.text;
      if (i > 0) text = text.replace(/^\r?\n/, "");
      if (i < out.length - 1) text = text.replace(/\r?\n$/, "");
      return { ...seg, text };
    })
    .filter((seg) => seg.code || seg.text !== "");
}

function renderMarkdownBlocks(src: string): ReactNode {
  const segments = splitFences(src);
  if (segments.some((seg) => seg.code)) {
    return (
      <div className="text-md leading-6 text-fg-primary space-y-0.5">
        {segments.map((seg, i) =>
          seg.code ? (
            <CodeBlock key={`f${i}`} body={seg.text} language={seg.language} />
          ) : (
            <div key={`f${i}`}>{renderMarkdownBlocks(seg.text)}</div>
          ),
        )}
      </div>
    );
  }
  // Split into paragraphs / lists. Keep dependency-free; full MD lands when DOMPurify+marked arrives.
  const lines = src.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let bullet: string[] = [];
  const flushBullet = () => {
    if (bullet.length === 0) return;
    blocks.push(
      <ul key={`u${blocks.length}`} className="list-disc pl-5 space-y-0.5 my-1">
        {bullet.map((b, i) => (
          <li key={i}>{renderInline(b)}</li>
        ))}
      </ul>,
    );
    bullet = [];
  };
  let quote: string[] = [];
  const flushQuote = () => {
    if (quote.length === 0) return;
    blocks.push(
      <blockquote
        key={`q${blocks.length}`}
        className="my-1 border-l-4 border-border-default pl-3 text-fg-secondary"
      >
        {renderMarkdownBlocks(quote.join("\n"))}
      </blockquote>,
    );
    quote = [];
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*>/.test(line)) {
      flushBullet();
      quote.push(line.replace(/^\s*> ?/, ""));
      continue;
    }
    flushQuote();
    if (/^\s*[-*]\s+/.test(line)) {
      bullet.push(line.replace(/^\s*[-*]\s+/, ""));
      continue;
    }
    flushBullet();
    if (line === "") {
      blocks.push(<div key={`s${blocks.length}`} className="h-2" aria-hidden />);
    } else {
      blocks.push(
        <p key={`p${blocks.length}`} className="whitespace-pre-wrap break-words">
          {renderInline(line)}
        </p>,
      );
    }
  }
  flushBullet();
  flushQuote();
  return <div className="text-md leading-6 text-fg-primary space-y-0.5">{blocks}</div>;
}

function CodeBlock({ body, language }: { body: string; language?: string }): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard?.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard may be unavailable in some contexts */
    }
  };
  return (
    <div className="my-1 rounded-md border border-border-subtle bg-surface-base overflow-hidden">
      <div className="flex items-center justify-between px-2.5 py-1 bg-surface-raised text-xs text-fg-muted">
        <span className="flex items-center gap-1.5">
          <CodeIcon size={12} aria-hidden />
          <span className="font-mono">{language || "text"}</span>
        </span>
        <button
          type="button"
          onClick={onCopy}
          aria-label={t("message.copy") as string}
          className="px-1.5 py-0.5 rounded hover:bg-canvas focus-visible:shadow-focus inline-flex items-center gap-1"
        >
          <Copy size={12} aria-hidden />
          <span>
            {copied ? (t("message.copied", { defaultValue: "Copied" }) as string) : (t("message.copy") as string)}
          </span>
        </button>
      </div>
      <pre className="px-3 py-2 text-[12.5px] font-mono text-fg-primary overflow-x-auto leading-5">
        <code>{body}</code>
      </pre>
    </div>
  );
}

function ImageGallery({
  attachments,
  caption,
}: {
  attachments: NonNullable<MessageView["attachments"]>;
  caption?: string;
}): JSX.Element {
  const [open, setOpen] = useState<number | null>(null);
  const len = attachments.length;
  const grid =
    len === 1
      ? "grid-cols-1 max-w-md"
      : len === 2
        ? "grid-cols-2 max-w-md"
        : "grid-cols-3 max-w-lg";
  return (
    <div className="my-1 space-y-1">
      <div className={cn("grid gap-1.5", grid)}>
        {attachments.map((a, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setOpen(i)}
            className="relative aspect-video bg-surface-raised rounded-md border border-border-subtle overflow-hidden focus-visible:shadow-focus"
            aria-label={a.name}
          >
            {a.url ? (
              <img
                src={a.url}
                alt={a.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-fg-muted">
                <ImageIcon size={24} aria-hidden />
              </div>
            )}
          </button>
        ))}
      </div>
      {caption ? <p className="text-sm text-fg-secondary">{caption}</p> : null}
      {open !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={attachments[open].name}
          className="fixed inset-0 z-50 bg-fg-primary/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setOpen(null)}
        >
          <img
            src={attachments[open].url}
            alt={attachments[open].name}
            className="max-h-[90vh] max-w-[90vw] rounded-md shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}

function VideoBlock({
  attachments,
}: {
  attachments: NonNullable<MessageView["attachments"]>;
}): JSX.Element {
  const a = attachments[0];
  return (
    <div className="my-1 max-w-md rounded-md border border-border-subtle overflow-hidden bg-fg-primary">
      {a.url ? (
        <video
          controls
          preload="metadata"
          className="w-full h-auto block"
          src={a.url}
          aria-label={a.name}
        />
      ) : (
        <div className="aspect-video flex items-center justify-center text-fg-inverse gap-2">
          <Film size={24} aria-hidden />
          <span className="text-sm">{a.name}</span>
        </div>
      )}
    </div>
  );
}

/**
 * iOS saves a downloaded video into Files, but the TikTok, YouTube and Threads apps
 * only pick from Photos. The share sheet's "Save Video" / "Save Image" puts it in Photos.
 * Android downloads already reach the gallery, so the plain download link is enough there.
 */
export function canSaveToPhotos(mime?: string): boolean {
  if (!mime || !(mime.startsWith("video/") || mime.startsWith("image/"))) return false;
  if (typeof navigator === "undefined" || typeof navigator.canShare !== "function" || typeof File === "undefined") return false;
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!ios) return false;
  try {
    return navigator.canShare({ files: [new File([], "check", { type: mime })] });
  } catch {
    return false;
  }
}

type SavePhase = "idle" | "loading" | "ready" | "error";

export function SaveToPhotosButton({ id, name, mime }: { id: string; name: string; mime: string }): JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<SavePhase>("idle");
  const share = async (f: File): Promise<void> => {
    try {
      await navigator.share({ files: [f] });
      setPhase("idle");
    } catch (e) {
      const reason = typeof e === "object" && e !== null && "name" in e ? String(e.name) : "";
      // Fetching a large video can outlast the tap's user activation; the next tap shares the file already fetched.
      setPhase(reason === "AbortError" ? "idle" : reason === "NotAllowedError" ? "ready" : "error");
    }
  };
  const onClick = async (): Promise<void> => {
    if (file) return share(file);
    setPhase("loading");
    try {
      const response = await authenticatedFetch(resolveApiUrl(`/v1/files/${encodeURIComponent(id)}/download`));
      if (!response.ok) throw new Error(String(response.status));
      const fetched = new File([await response.blob()], name, { type: mime });
      setFile(fetched);
      await share(fetched);
    } catch {
      setPhase("error");
    }
  };
  const label = phase === "loading" ? t("files.saveToPhotosPreparing")
    : phase === "ready" ? t("files.saveToPhotosReady")
      : phase === "error" ? t("files.saveToPhotosFailed")
        : t("files.saveToPhotos");
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={phase === "loading"}
      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-border-subtle text-xs text-fg-secondary hover:bg-surface-raised focus-visible:shadow-focus disabled:opacity-60"
    >
      <ImageDown size={14} aria-hidden />
      <span aria-live="polite">{label}</span>
    </button>
  );
}

function FileBlock({
  attachments,
}: {
  attachments: NonNullable<MessageView["attachments"]>;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <ul className="my-1 space-y-1.5 max-w-md">
      {attachments.map((a, i) => {
        const Icon = a.mime?.startsWith("image/")
          ? ImageIcon
          : a.mime?.startsWith("video/")
            ? Film
            : a.mime?.endsWith("zip") || a.mime?.endsWith("7z")
              ? FileArchive
              : FileText;
        return (
          <li
            key={i}
            className="flex items-center gap-2.5 p-2.5 rounded-md border border-border-subtle bg-surface-base"
          >
            <span className="w-9 h-9 rounded bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
              <Icon size={16} aria-hidden />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" title={a.name}>
                {a.name}
              </p>
              <p className="text-xs text-fg-muted">
                {typeof a.size === "number" ? formatBytes(a.size) : a.mime || "—"}
              </p>
            </div>
            {a.id && canSaveToPhotos(a.mime) ? (
              <SaveToPhotosButton id={a.id} name={a.name} mime={a.mime!} />
            ) : null}
            {a.url ? (
              <a
                href={a.url}
                download={a.name}
                aria-label={t("files.download") as string}
                className="p-1.5 rounded hover:bg-surface-raised text-fg-secondary focus-visible:shadow-focus"
              >
                <Download size={14} aria-hidden />
              </a>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function SystemRow({ message }: { message: MessageView }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-1 flex items-center gap-2 text-xs text-fg-muted">
      <Info size={12} aria-hidden />
      <span className="italic">{message.body}</span>
      <time dateTime={message.createdAt}>· {formatRelative(message.createdAt)}</time>
      <span className="sr-only">{t("message.system", { defaultValue: "system" })}</span>
    </div>
  );
}

function TaskCard({ message }: { message: MessageView }): JSX.Element {
  const { t } = useTranslation();
  const task = message.task;
  if (!task) return <p className="text-md">{message.body}</p>;
  const tone =
    task.status === "done"
      ? "success"
      : task.status === "review"
        ? "info"
        : task.status === "in_progress"
          ? "warning"
          : "neutral";
  return (
    <div className="my-1 max-w-md rounded-md border border-border-subtle bg-surface-base p-3 space-y-1">
      <div className="flex items-center gap-2">
        <CheckSquare size={14} aria-hidden className="text-accent-task" />
        <Badge tone={tone as "success" | "info" | "warning" | "neutral"}>
          {t(`tasks.status.${task.status === "in_progress" ? "inProgress" : task.status}` as const)}
        </Badge>
        {task.due ? (
          <span className="text-xs text-fg-muted">
            ~ {formatRelative(task.due)}
          </span>
        ) : null}
      </div>
      <p className="text-md font-medium">{task.title}</p>
      {message.body && message.body !== task.title ? (
        <p className="text-sm text-fg-secondary">{message.body}</p>
      ) : null}
    </div>
  );
}

function CallSummary({ message }: { message: MessageView }): JSX.Element {
  const { t } = useTranslation();
  const c = message.call;
  return (
    <div className="my-1 max-w-md rounded-md border border-border-subtle bg-brand-50/50 p-3 space-y-1.5">
      <div className="flex items-center gap-2 text-brand-700">
        <Phone size={14} aria-hidden />
        <span className="text-sm font-medium">
          {t("call.summaryTitle", { defaultValue: "通話サマリ" }) as string}
        </span>
        {c ? (
          <span className="text-xs text-fg-muted">
            {Math.round(c.durationSec / 60)} min · {c.participants} 名
          </span>
        ) : null}
      </div>
      {renderMarkdownBlocks(message.body)}
    </div>
  );
}

function AISummary({ message }: { message: MessageView }): JSX.Element {
  const { t } = useTranslation();
  const ai = message.aiSummary;
  return (
    <div className="my-1 max-w-md rounded-md border border-brand-100 bg-brand-50/40 p-3 space-y-1.5">
      <div className="flex items-center gap-2 text-accent-ai">
        <Sparkles size={14} aria-hidden />
        <Badge tone="ai">{t("ai.badge", { defaultValue: "AI" }) as string}</Badge>
      </div>
      {ai ? (
        <>
          <p className="text-md text-fg-primary">{ai.tldr}</p>
          {ai.bullets.length > 0 ? (
            <ul className="list-disc pl-5 text-sm text-fg-secondary space-y-0.5">
              {ai.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        renderMarkdownBlocks(message.body)
      )}
    </div>
  );
}

function PollCard({ message }: { message: MessageView }): JSX.Element {
  const poll = message.poll;
  if (!poll) return <p className="text-md">{message.body}</p>;
  const total = poll.options.reduce((s, o) => s + o.votes, 0) || 1;
  return (
    <div className="my-1 max-w-md rounded-md border border-border-subtle bg-surface-base p-3 space-y-2">
      <div className="flex items-center gap-2">
        <ListChecks size={14} aria-hidden className="text-brand-700" />
        <p className="text-sm font-medium">{poll.question}</p>
      </div>
      <ul className="space-y-1.5">
        {poll.options.map((o) => {
          const pct = Math.round((o.votes / total) * 100);
          const voted = poll.voted === o.id;
          return (
            <li
              key={o.id}
              className={cn(
                "relative rounded-md border px-2.5 py-1.5 text-sm",
                voted
                  ? "border-brand-600 bg-brand-50"
                  : "border-border-subtle bg-canvas",
              )}
            >
              <div
                aria-hidden
                className="absolute inset-y-0 left-0 rounded-md bg-brand-100/60"
                style={{ width: `${pct}%` }}
              />
              <div className="relative flex items-center justify-between">
                <span>{o.label}</span>
                <span className="text-xs text-fg-muted">
                  {o.votes} ({pct}%)
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AnnounceCard({ message }: { message: MessageView }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="my-1 rounded-md border-l-4 border-warning bg-warning-bg/40 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone size={14} aria-hidden className="text-warning" />
        <Badge tone="warning">
          {t("message.kinds.announce", { defaultValue: "重要連絡" }) as string}
        </Badge>
      </div>
      {renderMarkdownBlocks(message.body)}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

/**
 * Render a chat message — supports all 12 Chatwork-killer kinds.
 *
 *   - For the legacy seed data (no `kind` field), heuristically detect:
 *     code blocks (``` … ```), markdown image (`![…](url)`), or fall back to text.
 */
function detectKind(m: MessageView): MessageKind {
  if (isIntegrationMessage(m)) return "text";
  if (m.kind) return m.kind;
  const b = m.body ?? "";
  if (/^```/.test(b)) return "code";
  if (/^!\[/.test(b)) return "image";
  if (m.attachments && m.attachments.length > 0) {
    const first = m.attachments[0];
    if (first.mime?.startsWith("image/")) return "image";
    if (first.mime?.startsWith("video/")) return "video";
    return "file";
  }
  return "text";
}

function IntegrationPlainBody({ body }: { body: string }): JSX.Element {
  return (
    <div className="text-md leading-6 text-fg-primary">
      <p className="whitespace-pre-wrap break-words">{body}</p>
    </div>
  );
}

export function MessageItem({
  message,
  showHeader = true,
  unread = false,
  onRetry,
  onTaskify,
  onReply,
  onOpenThread,
  onReact,
  onEdit,
  onDelete,
  onPin,
  onCopy,
  onQuote,
  onReport,
  onBlock,
  isOwn = false,
}: MessageItemProps): JSX.Element {
  const { t } = useTranslation();
  const integration = isIntegrationMessage(message);
  const author = {
    displayName: integration
      ? (message.sender?.display_name || message.authorName || "アプリ")
      : (message.authorName || message.authorId),
    avatarUrl: undefined,
    presence: undefined,
  };
  const status = message.status;
  const kind = detectKind(message);

  // System rows render as a single dim line, no avatar / actions.
  // Integration posts are type=text and must not reuse this path.
  if (kind === "system" && !integration) {
    return <SystemRow message={message} />;
  }

  let body: ReactNode = null;
  if (integration) {
    body = <IntegrationPlainBody body={message.body} />;
  } else if (kind === "code" && message.kind !== "code") {
    // Detected only because the body starts with ```: the text renderer splits every fence
    // (mid-line fences and several blocks in one message included) and keeps the text around them.
    body = renderMarkdownBlocks(message.body);
  } else if (kind === "code") {
    // Strip the fenced wrapper if present.
    const m = message.body.match(/^```(\w+)?\n([\s\S]*?)\n?```$/);
    body = (
      <CodeBlock
        body={m ? m[2] : message.body}
        language={message.language ?? m?.[1]}
      />
    );
  } else if (kind === "image" && message.attachments && message.attachments.length > 0) {
    body = <ImageGallery attachments={message.attachments} caption={message.body} />;
  } else if (kind === "video" && message.attachments && message.attachments.length > 0) {
    body = <><VideoBlock attachments={message.attachments} /><FileBlock attachments={message.attachments} />{message.body && renderMarkdownBlocks(message.body)}</>;
  } else if (kind === "file" && message.attachments && message.attachments.length > 0) {
    body = <><FileBlock attachments={message.attachments} />{message.body && renderMarkdownBlocks(message.body)}</>;
  } else if (kind === "task") {
    body = <TaskCard message={message} />;
  } else if (kind === "call_summary") {
    body = <CallSummary message={message} />;
  } else if (kind === "ai_summary") {
    body = <AISummary message={message} />;
  } else if (kind === "poll") {
    body = <PollCard message={message} />;
  } else if (kind === "announce") {
    body = <AnnounceCard message={message} />;
  } else {
    body = renderMarkdownBlocks(message.body);
  }

  return (
    <article
      data-message-id={message.id}
      data-kind={kind}
      data-sender-kind={integration ? "integration" : "user"}
      className={cn(
        "group flex gap-2 px-4 py-1 hover:bg-surface-base relative",
        showHeader && "pt-2",
        status === "sending" && "opacity-60",
        status === "failed" && "bg-danger-bg/40",
        unread && "border-l-2 border-l-brand-600",
      )}
    >
      <div className="w-9 shrink-0 pt-0.5">
        {showHeader ? (
          <Avatar
            name={author?.displayName ?? "?"}
            size={36}
            presence={author?.presence}
          />
        ) : (
          <time
            dateTime={message.createdAt}
            className="block text-[10px] text-transparent group-hover:text-fg-muted text-right pr-1 leading-6 select-none"
            aria-hidden
          >
            {new Date(message.createdAt).toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </time>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {message.parentId && <a className="text-xs text-brand-700 underline" href={`/rooms/${encodeURIComponent(message.roomId)}?message=${encodeURIComponent(message.parentId)}`}>返信先のメッセージ</a>}
        {showHeader ? (
          <header className="flex items-baseline gap-2">
            <span className="font-semibold text-sm text-fg-primary">
              {author?.displayName ?? "Unknown"}
            </span>
            {integration ? (
              <Badge tone="neutral" className="text-[10px]">
                アプリ
              </Badge>
            ) : null}
            <time
              dateTime={message.createdAt}
              className="text-xs text-fg-muted"
              title={new Date(message.createdAt).toLocaleString()}
            >
              {formatRelative(message.createdAt)}
            </time>
            {message.editedAt ? (
              <span className="text-xs text-fg-muted italic" title={message.editedAt}>
                ({t("message.edited", { defaultValue: "編集済み" }) as string})
              </span>
            ) : null}
            {message.pinned ? (
              <Pin size={11} aria-hidden className="text-warning" />
            ) : null}
            {status === "sending" ? (
              <Badge tone="neutral" className="text-[10px]">
                {t("message.sending")}
              </Badge>
            ) : null}
            {status === "failed" ? (
              <Badge tone="danger" className="text-[10px]">
                <AlertCircle size={10} aria-hidden />
                {t("message.failed")}
              </Badge>
            ) : null}
          </header>
        ) : null}
        <div>{body}</div>
        {message.reactions && message.reactions.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                onClick={() => onReact?.(message.id)}
                className={cn(
                  "px-1.5 py-0.5 rounded-full border text-xs flex items-center gap-1 hover:border-brand-600 focus-visible:shadow-focus",
                  r.byMe
                    ? "border-brand-600 bg-brand-50 text-brand-700"
                    : "border-border-subtle bg-surface-raised text-fg-secondary",
                )}
                aria-pressed={r.byMe}
              >
                <span aria-hidden>{r.emoji}</span>
                <span>{r.count}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => onReact?.(message.id)}
              aria-label={t("message.react") as string}
              className="px-1.5 py-0.5 rounded-full border border-dashed border-border-default text-xs text-fg-muted hover:bg-surface-raised focus-visible:shadow-focus"
            >
              <Smile size={11} aria-hidden />
            </button>
          </div>
        ) : null}
        {typeof message.replyCount === "number" && message.replyCount > 0 ? (
          <button
            type="button"
            onClick={() => (onOpenThread ?? onReply)?.(message.threadRootId || message.id)}
            className="mt-1 inline-flex items-center gap-1 text-xs text-brand-700 hover:underline focus-visible:shadow-focus rounded"
          >
            <Reply size={11} aria-hidden />
            <span>
              {t("message.threadReplies", {
                count: message.replyCount,
                defaultValue: "{{count}} 件の返信",
              }) as string}
            </span>
          </button>
        ) : null}
        {status === "failed" && onRetry && message.localId ? (
          <button
            type="button"
            onClick={() => onRetry(message.localId!)}
            className="mt-1 inline-flex items-center gap-1 text-xs text-brand-700 hover:underline focus-visible:shadow-focus rounded"
          >
            <RotateCcw size={12} aria-hidden />
            {t("compose.retry")}
          </button>
        ) : null}
      </div>
      {/* Hover actions */}
      <HoverActions
        message={message}
        onReact={onReact}
        onReply={onReply}
        onTaskify={onTaskify}
        onEdit={integration ? undefined : onEdit}
        onDelete={onDelete}
        onPin={onPin}
        onCopy={onCopy}
        onQuote={onQuote}
        onReport={isOwn ? undefined : onReport}
        onBlock={isOwn ? undefined : onBlock}
      />
    </article>
  );
}

function HoverActions({
  message,
  onReact,
  onReply,
  onTaskify,
  onEdit,
  onDelete,
  onPin,
  onCopy,
  onQuote,
  onReport,
  onBlock,
}: {
  message: MessageView;
  onReact?: (id: string) => void;
  onReply?: (id: string) => void;
  onTaskify?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onPin?: (id: string) => void;
  onCopy?: (id: string) => void;
  onQuote?: (id: string) => void;
  onReport?: (id: string) => void;
  onBlock?: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity absolute -top-3 right-3 flex items-center gap-0.5 rounded-md bg-canvas border border-border-subtle shadow-md">
      <ActionButton onClick={() => onReact?.(message.id)} label={t("message.react") as string}>
        <Smile size={14} aria-hidden />
      </ActionButton>
      <ActionButton onClick={() => onReply?.(message.id)} label={t("message.reply") as string}>
        <MessageSquare size={14} aria-hidden />
      </ActionButton>
      <ActionButton onClick={() => onTaskify?.(message.id)} label={t("message.task") as string}>
        <CheckSquare size={14} aria-hidden />
      </ActionButton>
      <div className="relative" ref={ref}>
        <ActionButton
          onClick={() => setOpen((v) => !v)}
          label={t("message.more", { defaultValue: "その他" }) as string}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreHorizontal size={14} aria-hidden />
        </ActionButton>
        {open ? (
          <div
            role="menu"
            className="absolute top-full right-0 mt-1 min-w-44 bg-canvas border border-border-subtle rounded-md shadow-lg py-1 text-sm z-30"
          >
            <MenuItem
              icon={<Pin size={13} aria-hidden />}
              label={
                message.pinned
                  ? (t("message.unpin", { defaultValue: "ピン解除" }) as string)
                  : (t("message.pin", { defaultValue: "ピン留め" }) as string)
              }
              onClick={() => {
                onPin?.(message.id);
                setOpen(false);
              }}
            />
            <MenuItem
              icon={<Quote size={13} aria-hidden />}
              label={t("message.quote") as string}
              onClick={() => {
                onQuote?.(message.id);
                setOpen(false);
              }}
            />
            <MenuItem
              icon={<Copy size={13} aria-hidden />}
              label={t("message.copy") as string}
              onClick={() => {
                onCopy?.(message.id);
                setOpen(false);
              }}
            />
            {onEdit ? (
            <MenuItem
              icon={<Pencil size={13} aria-hidden />}
              label={t("message.edit") as string}
              onClick={() => {
                onEdit(message.id);
                setOpen(false);
              }}
            />
            ) : null}
            <div className="my-1 border-t border-border-subtle" aria-hidden />
            {onReport ? (
              <MenuItem
                icon={<Flag size={13} aria-hidden />}
                label="通報する"
                onClick={() => {
                  onReport(message.id);
                  setOpen(false);
                }}
              />
            ) : null}
            {onBlock ? (
              <MenuItem
                icon={<Ban size={13} aria-hidden />}
                label="ブロックする"
                onClick={() => {
                  onBlock(message.id);
                  setOpen(false);
                }}
              />
            ) : null}
            <MenuItem
              icon={<Trash2 size={13} aria-hidden />}
              label={t("message.delete") as string}
              tone="danger"
              onClick={() => {
                onDelete?.(message.id);
                setOpen(false);
              }}
            />
          </div>
        ) : null}
      </div>
      {message.status === "sent" ? (
        <span
          className="px-1.5 py-1 text-success"
          role="img"
          aria-label={t("message.sent") as string}
          title={t("message.sent") as string}
        >
          <CheckCheck size={14} aria-hidden />
        </span>
      ) : message.status === "sending" ? (
        <span
          className="px-1.5 py-1 text-fg-muted"
          role="img"
          aria-label={t("message.sending") as string}
        >
          <Check size={14} aria-hidden />
        </span>
      ) : null}
    </div>
  );
}

function ActionButton({
  children,
  label,
  onClick,
  ...rest
}: {
  children: React.ReactNode;
  label: string;
  onClick?: () => void;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-haspopup" | "aria-expanded">): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="px-1.5 py-1 hover:bg-surface-raised text-fg-secondary focus-visible:shadow-focus"
      {...rest}
    >
      {children}
    </button>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  tone = "default",
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-raised focus-visible:bg-surface-raised focus:outline-none",
        tone === "danger" ? "text-danger" : "text-fg-primary",
      )}
    >
      <span className="text-fg-muted">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
